import React, { useState, useEffect, useCallback } from "react";
import { getDatabase, ref, get, onValue, remove, off } from "firebase/database";
import { toast } from "react-toastify";

const PAST_SCHEDULE_LOOKBACK_DAYS = 7;
const FUTURE_SCHEDULE_LOOKAHEAD_DAYS = 120;

const getVietnamDateKey = (date) => {
  const vietnamDate = new Date(date.getTime() + 7 * 60 * 60 * 1000);
  return `${vietnamDate.getUTCFullYear()}-${String(
    vietnamDate.getUTCMonth() + 1
  ).padStart(2, "0")}-${String(vietnamDate.getUTCDate()).padStart(2, "0")}`;
};

const getDateKeyByOffset = (offsetDays) => {
  const date = new Date();
  date.setDate(date.getDate() + offsetDays);
  return getVietnamDateKey(date);
};

const mapScheduledData = (data = {}) => {
  const allPosts = [];

  for (const dateKey in data) {
    const postsByDate = data[dateKey] || {};
    for (const postId in postsByDate) {
      allPosts.push({
        id: postId,
        dateKey,
        ...postsByDate[postId],
      });
    }
  }

  allPosts.sort((a, b) => {
    const order = { pending: 0, failed: 1, posted: 2 };
    const diff = (order[a.status] || 0) - (order[b.status] || 0);
    if (diff !== 0) return diff;
    const dtA = `${a.dateKey} ${a.scheduleTime}`;
    const dtB = `${b.dateKey} ${b.scheduleTime}`;
    return dtB > dtA ? 1 : dtB < dtA ? -1 : 0;
  });

  return allPosts;
};

const ScheduledPostsList = () => {
  const [scheduledPosts, setScheduledPosts] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [cancellingId, setCancellingId] = useState(null);
  const [filter, setFilter] = useState("all"); // all | pending | posted | failed

  const userId = localStorage.getItem("userId");

  useEffect(() => {
    if (!userId) {
      setIsLoading(false);
      return;
    }

    const db = getDatabase();
    const scheduledRef = ref(db, `userScheduledNormalPosts/${userId}`);
    setIsLoading(true);

    const loadLegacySchedules = async () => {
      const dateKeys = [];
      for (
        let offset = -PAST_SCHEDULE_LOOKBACK_DAYS;
        offset <= FUTURE_SCHEDULE_LOOKAHEAD_DAYS;
        offset += 1
      ) {
        dateKeys.push(getDateKeyByOffset(offset));
      }

      const snapshots = await Promise.all(
        dateKeys.map((dateKey) =>
          get(ref(db, `scheduledNormalPosts/${dateKey}/${userId}`)).then(
            (snapshot) => [dateKey, snapshot]
          )
        )
      );

      const legacyData = snapshots.reduce((acc, [dateKey, snapshot]) => {
        if (snapshot.exists()) {
          acc[dateKey] = snapshot.val();
        }
        return acc;
      }, {});

      setScheduledPosts(mapScheduledData(legacyData));
      setIsLoading(false);
    };

    const handleSnapshot = (snapshot) => {
      if (snapshot.exists()) {
        setScheduledPosts(mapScheduledData(snapshot.val() || {}));
        setIsLoading(false);
        return;
      }

      loadLegacySchedules().catch((error) => {
        console.error("Lỗi khi tải lịch đăng bài:", error);
        setScheduledPosts([]);
        setIsLoading(false);
      });
    };

    onValue(scheduledRef, handleSnapshot);
    return () => off(scheduledRef, "value", handleSnapshot);
  }, [userId]);

  const handleCancel = useCallback(
    async (post) => {
      setCancellingId(post.id);
      try {
        const db = getDatabase();
        await Promise.all([
          remove(
            ref(db, `scheduledNormalPosts/${post.dateKey}/${userId}/${post.id}`)
          ),
          remove(
            ref(
              db,
              `userScheduledNormalPosts/${userId}/${post.dateKey}/${post.id}`
            )
          ),
        ]);
        toast.success("Đã hủy lịch đăng bài!", {
          position: "top-right",
          autoClose: 2000,
        });
      } catch (error) {
        console.error("Lỗi khi hủy lịch:", error);
        toast.error("Không thể hủy. Vui lòng thử lại!", {
          position: "top-right",
          autoClose: 3000,
        });
      } finally {
        setCancellingId(null);
      }
    },
    [userId]
  );

  const pendingCount = scheduledPosts.filter((p) => p.status === "pending").length;
  const filteredPosts =
    filter === "all"
      ? scheduledPosts
      : scheduledPosts.filter((p) => p.status === filter);

  const formatDate = (dk) => {
    try {
      const [y, m, d] = dk.split("-");
      return `${d}/${m}/${y}`;
    } catch {
      return dk;
    }
  };

  const isToday = (dk) => {
    const now = new Date(new Date().getTime() + 7 * 60 * 60 * 1000);
    const today = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, "0")}-${String(now.getUTCDate()).padStart(2, "0")}`;
    return dk === today;
  };

  if (isLoading || scheduledPosts.length === 0) return null;

  return (
    <>
      {/* Floating trigger button */}
      <div className="flex justify-center my-3">
        <button
          onClick={() => setIsOpen(true)}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 rounded-full shadow-sm hover:shadow-md hover:border-blue-300 transition-all duration-200 text-sm font-medium text-gray-700 hover:text-blue-600 group"
        >
          <svg
            className="w-4 h-4 text-blue-500 group-hover:text-blue-600 transition-colors"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          Bài viết đã lên lịch
          {pendingCount > 0 && (
            <span className="flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-blue-500 text-white text-xs font-bold rounded-full">
              {pendingCount}
            </span>
          )}
        </button>
      </div>

      {/* Modal overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
          onClick={() => setIsOpen(false)}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />

          {/* Modal */}
          <div
            className="relative w-full sm:max-w-2xl h-[85vh] bg-white sm:rounded-2xl rounded-t-2xl shadow-2xl flex flex-col overflow-hidden animate-slideUp"
            onClick={(e) => e.stopPropagation()}
            style={{
              animation: "slideUp 0.25s ease-out",
            }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-blue-500 flex items-center justify-center shadow-sm">
                  <svg
                    className="w-5 h-5 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <div>
                  <h2 className="text-base font-semibold text-gray-800">
                    Bài viết đã lên lịch
                  </h2>
                  <p className="text-xs text-gray-500">
                    {scheduledPosts.length} bài viết
                    {pendingCount > 0 &&
                      ` · ${pendingCount} đang chờ`}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Filter tabs */}
            <div className="flex gap-1 px-5 py-2.5 bg-gray-50 border-b border-gray-100">
              {[
                { key: "all", label: "Tất cả", count: scheduledPosts.length },
                { key: "pending", label: "Đang chờ", count: scheduledPosts.filter((p) => p.status === "pending").length },
                { key: "posted", label: "Đã đăng", count: scheduledPosts.filter((p) => p.status === "posted").length },
                { key: "failed", label: "Thất bại", count: scheduledPosts.filter((p) => p.status === "failed").length },
              ]
                .filter((t) => t.count > 0)
                .map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setFilter(tab.key)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all ${
                      filter === tab.key
                        ? "bg-blue-500 text-white shadow-sm"
                        : "text-gray-500 hover:bg-gray-200"
                    }`}
                  >
                    {tab.label} ({tab.count})
                  </button>
                ))}
            </div>

            {/* Posts list */}
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2.5">
              {filteredPosts.length === 0 ? (
                <div className="text-center py-10 text-gray-400">
                  <svg
                    className="w-12 h-12 mx-auto mb-3 text-gray-300"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                    />
                  </svg>
                  <p className="text-sm">Không có bài viết nào</p>
                </div>
              ) : (
                filteredPosts.map((post) => (
                  <PostCard
                    key={`${post.dateKey}-${post.id}`}
                    post={post}
                    formatDate={formatDate}
                    isToday={isToday}
                    onCancel={handleCancel}
                    isCancelling={cancellingId === post.id}
                  />
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Animation keyframe */}
      <style>{`
        @keyframes slideUp {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>
    </>
  );
};

// Extracted card component
const PostCard = ({ post, formatDate, isToday, onCancel, isCancelling }) => {
  const [showFull, setShowFull] = useState(false);

  const statusMap = {
    pending: {
      bg: "bg-amber-50",
      border: "border-amber-200",
      badge: "bg-amber-100 text-amber-700",
      dot: "bg-amber-400",
      label: "Đang chờ",
    },
    posted: {
      bg: "bg-emerald-50",
      border: "border-emerald-200",
      badge: "bg-emerald-100 text-emerald-700",
      dot: "bg-emerald-400",
      label: "Đã đăng",
    },
    failed: {
      bg: "bg-red-50",
      border: "border-red-200",
      badge: "bg-red-100 text-red-700",
      dot: "bg-red-400",
      label: "Thất bại",
    },
  };

  const s = statusMap[post.status] || statusMap.pending;
  const contentPreview = post.content || "";
  const isLong = contentPreview.length > 300;

  return (
    <div
      className={`rounded-xl border ${s.border} ${s.bg} p-3.5 transition-all hover:shadow-sm`}
    >
      {/* Top row: date/time + status */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
            <svg
              className="w-3.5 h-3.5 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            {post.scheduleTime}
          </div>
          <span className="text-xs text-gray-400">·</span>
          <span className="text-xs text-gray-500">
            {isToday(post.dateKey) ? (
              <span className="text-blue-600 font-medium">Hôm nay</span>
            ) : (
              formatDate(post.dateKey)
            )}
          </span>
        </div>

        <span
          className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${s.badge}`}
        >
          <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
          {s.label}
        </span>
      </div>

      {/* Content */}
      <div className="mb-2">
        <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">
          {showFull
            ? contentPreview
            : contentPreview.substring(0, 300) + (isLong ? "..." : "")}
        </p>
        {isLong && (
          <button
            onClick={() => setShowFull(!showFull)}
            className="text-xs text-blue-500 hover:text-blue-600 mt-1 font-medium"
          >
            {showFull ? "Thu gọn" : "Xem thêm"}
          </button>
        )}
      </div>

      {/* Image thumbnails */}
      {post.images && post.images.length > 0 && (
        <div className="flex gap-1.5 mb-2 overflow-x-auto">
          {post.images.slice(0, 4).map((img, idx) => (
            <img
              key={idx}
              src={img}
              alt=""
              className="w-12 h-12 rounded-lg object-cover border border-white shadow-sm flex-shrink-0"
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          ))}
          {post.images.length > 4 && (
            <div className="w-12 h-12 rounded-lg bg-gray-200 flex items-center justify-center text-xs text-gray-500 font-medium flex-shrink-0">
              +{post.images.length - 4}
            </div>
          )}
        </div>
      )}

      {/* Fanpages */}
      {post.selectedFanpages && post.selectedFanpages.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {post.selectedFanpages.map((fp, idx) => (
            <span
              key={idx}
              className="inline-flex items-center gap-1 text-xs bg-white/80 text-gray-600 px-2 py-0.5 rounded-full border border-gray-200/60"
            >
              <svg
                className="w-3 h-3 text-blue-400"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z" />
              </svg>
              {fp.name}
            </span>
          ))}
        </div>
      )}

      {/* Error */}
      {post.status === "failed" && (post.errors || post.error) && (
        <div className="flex items-start gap-1.5 text-xs text-red-600 bg-red-100/50 rounded-lg px-2.5 py-1.5 mb-2">
          <svg
            className="w-3.5 h-3.5 mt-0.5 flex-shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
            />
          </svg>
          <span>{post.errors || post.error}</span>
        </div>
      )}

      {/* Cancel button for pending */}
      {post.status === "pending" && (
        <button
          onClick={() => onCancel(post)}
          disabled={isCancelling}
          className="w-full flex items-center justify-center gap-1.5 py-2 mt-1 text-xs font-medium text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 hover:border-red-300 transition-all disabled:opacity-50"
        >
          {isCancelling ? (
            <svg
              className="w-3.5 h-3.5 animate-spin"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
              />
            </svg>
          ) : (
            <svg
              className="w-3.5 h-3.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
          )}
          {isCancelling ? "Đang hủy..." : "Hủy lịch đăng bài"}
        </button>
      )}
    </div>
  );
};

export default ScheduledPostsList;
